/** Staff Dashboard → Notification Permissions: which roles get which automated emails/texts. Admins edit, managers view. */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BellRing, ChevronDown, Loader2, Mail, MessageSquare, Save, AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { formatApiError } from '@/utils/apiError';

const CH = { email: { label: 'Email', Icon: Mail }, sms: { label: 'Text', Icon: MessageSquare } };

function MuteSection({ group, people, roles, rolesOrder, roleLabels, canEdit, onMute }) {
  const [busy, setBusy] = useState(false);
  const eligible = (c) => people.filter((p) => roles[p.role]?.[group.key]?.[c] && (c === 'email' || p.has_mobile));
  const mutedFor = (c) => people.filter((p) => p.mutes?.[group.key]?.[c]);
  const anyRoleOn = rolesOrder.some((r) => group.channels.some((c) => roles[r][group.key][c]));
  const doMute = async (userId, channel, muted) => {
    setBusy(true);
    try { await onMute(userId, group.key, channel, muted); } finally { setBusy(false); }
  };
  if (!anyRoleOn && group.channels.every((c) => mutedFor(c).length === 0)) return null;
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2.5 space-y-2" data-testid={`np-mutes-${group.key}`}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Mute for individuals</div>
      {group.channels.map((c) => {
        const muted = mutedFor(c);
        const { Icon } = CH[c];
        return (
          <div key={c} className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`np-mutes-${group.key}-${c}`}>
            <span className="inline-flex items-center gap-1 font-semibold text-gray-700 w-14"><Icon className="w-3.5 h-3.5" /> {CH[c].label}</span>
            {muted.length === 0 && <span className="text-gray-400 italic">nobody muted</span>}
            {muted.map((p) => (
              <span key={p.user_id} className="inline-flex items-center gap-1 bg-rose-50 text-rose-800 border border-rose-200 rounded-full pl-2 pr-1 py-0.5" data-testid={`np-muted-${group.key}-${c}-${p.user_id}`}>
                {p.name} <span className="text-[10px] text-rose-500">({roleLabels[p.role]})</span>
                {canEdit && <button type="button" disabled={busy} onClick={() => doMute(p.user_id, c, false)} className="rounded-full hover:bg-rose-200 p-0.5" title="Unmute" data-testid={`np-unmute-${group.key}-${c}-${p.user_id}`}><X className="w-3 h-3" /></button>}
              </span>
            ))}
            {canEdit && (
              <select value="" disabled={busy} data-testid={`np-mute-pick-${group.key}-${c}`}
                onChange={(e) => e.target.value && doMute(e.target.value, c, true)}
                className="h-7 text-xs border rounded-md px-1.5 bg-white text-gray-700 ml-auto max-w-[180px]">
                <option value="">Mute someone…</option>
                {eligible(c).filter((p) => !p.mutes?.[group.key]?.[c]).map((p) => <option key={p.user_id} value={p.user_id}>{p.name} ({roleLabels[p.role]})</option>)}
              </select>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-gray-500">Muting only affects this group — the person keeps every other alert their role allows.</p>
    </div>
  );
}

function GroupRow({ group, roles, roleLabels, rolesOrder, summary, canEdit, onToggle, people, onMute }) {
  const [open, setOpen] = useState(false);
  const onCount = rolesOrder.reduce((n, r) => n + group.channels.filter((c) => roles[r][group.key][c]).length, 0);
  return (
    <Card className="overflow-hidden" data-testid={`np-group-${group.key}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50" data-testid={`np-group-toggle-${group.key}`}>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        <div className="flex-1 min-w-0">
          <div className="font-bold text-gray-900">{group.label}</div>
          <div className="text-xs text-gray-500">{group.items.length} alert{group.items.length === 1 ? '' : 's'} · {onCount === 0 ? <span className="text-amber-700 font-semibold">nobody receives these yet</span> : `${onCount} switch${onCount === 1 ? '' : 'es'} on`}</div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5">
          {group.channels.map((c) => {
            const s = summary?.[group.key]?.[c];
            const { Icon } = CH[c];
            return <span key={c} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${s?.count ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`} title={`${s?.names?.join(', ') || 'No one'}${s?.muted?.length ? ` · muted: ${s.muted.join(', ')}` : ''}`} data-testid={`np-summary-${group.key}-${c}`}><Icon className="w-3 h-3" /> {s?.count ?? 0}{s?.muted?.length ? <span className="text-rose-600 font-semibold">−{s.muted.length}</span> : null}</span>;
          })}
        </div>
      </button>
      {open && (
        <div className="border-t bg-gray-50/60 px-4 py-3 space-y-4">
          <ul className="text-sm space-y-1.5">
            {group.items.map((it) => (
              <li key={it.label} className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium text-gray-800">{it.label}</span>
                <span className="inline-flex gap-1">{it.channels.map((c) => { const { Icon } = CH[c]; return <Icon key={c} className="w-3.5 h-3.5 text-gray-400 inline" />; })}</span>
                {it.detail && <span className="text-xs text-gray-500 basis-full sm:basis-auto">{it.detail}</span>}
              </li>
            ))}
          </ul>
          <div className="grid gap-2" style={{ gridTemplateColumns: `minmax(90px,1fr) repeat(${group.channels.length}, auto)` }}>
            <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold self-end">Who gets it</div>
            {group.channels.map((c) => <div key={c} className="text-[11px] uppercase tracking-wider text-gray-500 font-bold text-center px-2">{CH[c].label}</div>)}
            {rolesOrder.map((r) => (
              <React.Fragment key={r}>
                <div className="font-semibold text-gray-800 text-sm self-center">{roleLabels[r]}</div>
                {group.channels.map((c) => (
                  <div key={c} className="flex justify-center items-center px-2">
                    <Switch checked={!!roles[r][group.key][c]} disabled={!canEdit} onCheckedChange={(v) => onToggle(r, group.key, c, v)} data-testid={`np-switch-${group.key}-${r}-${c}`} />
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
          {group.channels.includes('sms') && <p className="text-[11px] text-gray-500 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Texts only reach people with a UK mobile on their account.</p>}
          {people && <MuteSection group={group} people={people} roles={roles} rolesOrder={rolesOrder} roleLabels={roleLabels} canEdit={canEdit} onMute={onMute} />}
        </div>
      )}
    </Card>
  );
}

export default function NotificationPermissions() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState(null);
  const [people, setPeople] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPeople = () => fetch(`${API_URL}/api/staff/notify-permissions/mutes`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : { people: [] })).then((d) => setPeople(d.people || [])).catch(() => setPeople([]));

  useEffect(() => { if (user && !['admin', 'manager'].includes(user.role)) navigate('/staff', { replace: true }); }, [user, navigate]);
  useEffect(() => {
    if (!user) return;
    fetch(`${API_URL}/api/staff/notify-permissions`, { credentials: 'include' }).then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not load');
      setData(d); setRoles(d.roles);
    }).catch((e) => toast.error(e.message));
    loadPeople();
  }, [user]);

  const onMute = async (userId, group, channel, muted) => {
    const who = people?.find((x) => x.user_id === userId)?.name || 'Person';
    try {
      const r = await fetch(`${API_URL}/api/staff/users/${userId}/notify-mute`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ group, channel, muted }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not update');
      setPeople((p) => p.map((x) => (x.user_id === userId ? { ...x, mutes: d.mutes } : x)));
      setData((p) => ({ ...p, summary: d.summary }));
      toast.success(muted ? `${who} muted for this group` : `${who} unmuted`);
    } catch (e) { toast.error(e.message); }
  };

  const onToggle = (r, g, c, v) => { setRoles((p) => ({ ...p, [r]: { ...p[r], [g]: { ...p[r][g], [c]: v } } })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/staff/notify-permissions`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ roles }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not save');
      setData((p) => ({ ...p, ...d })); setRoles(d.roles); setDirty(false);
      toast.success('Notification permissions saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b sticky top-0 z-20 shadow-sm bg-[#27292E] text-white">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Link to="/staff" className="text-white/80 hover:text-white" data-testid="np-back"><ArrowLeft className="w-5 h-5" /></Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center"><BellRing className="w-4 h-4" /></div>
          <h1 className="text-base sm:text-lg font-bold truncate flex-1">Notification Permissions</h1>
          {data?.can_edit && (
            <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold" data-testid="np-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1" /> : <Save className="w-4 h-4 sm:mr-1" />}<span className="hidden sm:inline">Save changes</span>
            </Button>
          )}
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-5 space-y-3">
        <p className="text-sm text-gray-600" data-testid="np-intro">Choose which login roles receive each group of automated emails and texts. Everything starts <strong>off</strong> — switch a group on for a role and everyone with that role gets it.{data && !data.can_edit && <span className="text-amber-700 font-semibold"> You're viewing only — admins can change these.</span>}</p>
        {data?.summary?.no_mobile?.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="np-no-mobile">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span><strong>No mobile on file:</strong> {data.summary.no_mobile.join(', ')} — they can't receive texts until they add one on their dashboard.</span>
          </div>
        )}
        {!data || !roles ? <div className="py-10 flex justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <>
            <div className="text-xs text-gray-500 flex flex-wrap gap-x-3" data-testid="np-role-counts">{data.roles_order.map((r) => <span key={r}><strong>{data.role_labels[r]}</strong>: {data.summary.staff_by_role[r]} {data.summary.staff_by_role[r] === 1 ? 'person' : 'people'}</span>)}</div>
            {data.groups.map((g) => <GroupRow key={g.key} group={g} roles={roles} roleLabels={data.role_labels} rolesOrder={data.roles_order} summary={data.summary.groups} canEdit={data.can_edit} onToggle={onToggle} people={people} onMute={onMute} />)}
            <p className="text-[11px] text-gray-500 pt-2">Director reports (Friday profit text, weekly hours email, Thursday cost reminder) keep their own recipient lists in the Profit Centre.</p>
          </>
        )}
      </div>
      {dirty && data?.can_edit && (
        <div className="fixed bottom-4 inset-x-0 flex justify-center px-4 sm:hidden">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold shadow-lg" data-testid="np-save-mobile">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save changes</Button>
        </div>
      )}
    </div>
  );
}
